import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { format, isToday, isYesterday } from 'date-fns';
import messageService from '../../services/messageService';

const getInitials = (name) => {
  if (!name) return '??';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);

    if (isToday(date)) {
      // Today: show time like "10:30 AM"
      return format(date, 'h:mm a');
    } else if (isYesterday(date)) {
      // Yesterday
      return 'Yesterday';
    } else {
      // Older: show date like "Jan 28"
      return format(date, 'MMM d');
    }
  } catch {
    return '';
  }
};

const MessagesModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);

  const rolePrefix = location.pathname.split('/')[1] || 'player';
  const messagesPath = `/${rolePrefix}/messages`;

  // Fetch conversations when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchConversations();
    }
  }, [isOpen]);

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const response = await messageService.getMyConversations();
      // Get only top 5 conversations
      const top5 = (response.data || []).slice(0, 5);
      setConversations(top5);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const handleMessageClick = (conversation) => {
    onClose();
    // Navigate with full conversation object
    navigate(messagesPath, {
      state: {
        selectedMessageId: conversation._id,
        selectedConversation: conversation
      }
    });
  };

  const handleViewAllMessages = () => {
    onClose();
    navigate(messagesPath);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
        onClick={onClose}
        style={{ pointerEvents: 'auto' }}
      />

      {/* Modal */}
      <div
        className="fixed top-20 right-4 sm:right-8 w-[90vw] sm:w-[400px] max-w-[450px] bg-jp-field backdrop-blur-md rounded-2xl shadow-2xl z-[101] border border-jp-line max-h-[80vh] flex flex-col"
        style={{ pointerEvents: 'auto' }}
      >
        {/* Header */}
        <div className="p-4 border-b border-jp-line">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-jp-ink">Your messages</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-jp-section rounded-lg transition-colors text-jp-muted hover:text-jp-ink"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Messages List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-8 h-8 border-2 border-jp-accent border-t-transparent rounded-full animate-spin" />
            </div>
          ) : conversations.length > 0 ? (
            conversations.map((convo) => {
              const participant = convo.participant || {};
              const displayName = `${participant.firstName || ''} ${participant.lastName || ''}`.trim() || 'Unknown';
              const initials = getInitials(displayName);
              const hasUnread = (convo.unreadCount || 0) > 0;

              return (
                <div
                  key={convo._id}
                  onClick={() => handleMessageClick(convo)}
                  className={`flex items-center gap-3 p-4 hover:bg-jp-section transition-colors cursor-pointer border-b border-jp-line ${
                    hasUnread ? 'bg-blue-900/10' : ''
                  }`}
                >
                  {/* Avatar */}
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold text-sm overflow-hidden">
                      {participant.profileImage ? (
                        <img src={participant.profileImage} alt={displayName} className="w-full h-full object-cover" />
                      ) : (
                        initials
                      )}
                    </div>
                  </div>

                  {/* Message Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className={`text-sm truncate ${hasUnread ? 'text-jp-ink font-semibold' : 'text-jp-ink font-medium'}`}>
                        {displayName}
                      </h3>
                      <span className="text-jp-muted/80 text-xs ml-2 flex-shrink-0">
                        {formatTime(convo.lastMessage?.createdAt || convo.updatedAt)}
                      </span>
                    </div>
                    <p className={`text-xs truncate ${hasUnread ? 'text-blue-300 font-medium' : 'text-jp-muted/80'}`}>
                      {convo.lastMessage?.text || 'No messages yet'}
                    </p>
                  </div>

                  {/* Unread Indicator */}
                  {hasUnread && (
                    <div className="flex-shrink-0">
                      <span className="w-5 h-5 bg-cyan-500 text-slate-900 text-xs rounded-full flex items-center justify-center font-bold">
                        {convo.unreadCount}
                      </span>
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="flex items-center justify-center h-40 p-4">
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 bg-jp-section rounded-full flex items-center justify-center">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-jp-muted/80" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-jp-muted text-sm">No conversations yet</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-jp-line">
          <button
            onClick={handleViewAllMessages}
            className="w-full py-2 text-jp-accent hover:text-jp-accent/80 text-sm font-medium transition-colors"
          >
            View All Messages
          </button>
        </div>
      </div>
    </>
  );
};

export default MessagesModal;
